import { ErrorMessage } from "@components/donate/DonateV3/PaymentProcess/pages/Donate";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import { DonateFormType } from "@components/donate/DonateV3/types";
import styled from "@emotion/styled";
import Big from "big.js";
import { useContext, useEffect, useState } from "react";
import { FieldError, FormState, UseFormReturn } from "react-hook-form";

import {
  FundraiserResponse,
  Uuid,
} from "@every.org/common/src/codecs/entities";
import { joinEnglishWordSeries } from "@every.org/common/src/helpers/string";

import { NonprofitsContext } from "src/context/NonprofitsContext";
import { getNonprofitOrUndefined } from "src/context/NonprofitsContext/selectors";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { FundraiserNonprofitsPopover } from "src/pages/Fundraiser/FundraiserNonprofitsPopover";
import { colorCssVars } from "src/theme/color";
import { verticalStackCss } from "src/theme/spacing";
import { FontWeight } from "src/theme/text";

interface ToNonptofitsWeightsProps {
  fundraiser: FundraiserResponse;
  nonprofit: ContextNonprofit;
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
}

const useInitializeWeights = (
  fundraiser: FundraiserResponse,
  form: UseFormReturn<DonateFormType>
) => {
  const { toNonprofits, toNonprofitWeights } = form.watch();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const ids = fundraiser.forNonprofitIds;
    if (initialized || !ids || ids.length === 0) {
      return;
    }

    const weights = calculateWeights(
      ids,
      fundraiser.metadata?.forNonprofitWeights
    );
    if (!toNonprofits) {
      form.setValue("toNonprofits", ids);
    }
    if (!toNonprofitWeights) {
      form.setValue("toNonprofitWeights", weights);
    }
    setInitialized(true);
  }, [
    initialized,
    form,
    fundraiser.forNonprofitIds,
    fundraiser.metadata?.forNonprofitWeights,
    toNonprofitWeights,
    toNonprofits,
  ]);

  return initialized;
};

function calculateWeights(ids: string[], existingWeights?: number[] | null) {
  if (ids.length === 0) {
    return [];
  }
  if (existingWeights && existingWeights.length === ids.length) {
    return existingWeights;
  }

  const weightPerId = new Big(100).div(ids.length).round(2);
  const weights: Big[] = new Array(ids.length).fill(weightPerId);

  let sum = new Big(0);

  for (const weight of weights) {
    sum = sum.add(weight);
  }

  // If the sum is not 100, add the difference to the first weight
  const diff = new Big(100).minus(sum);
  weights[0] = weights[0].plus(diff);

  return weights.map((weight) => weight.toNumber());
}

export function getErrorMessage(errors: FormState<DonateFormType>["errors"]) {
  // because "toNonprofits" has an array type,
  // the type of errors for "toNonprofits" will be an array of FieldError
  // but it not actually FieldError[], it is just FieldError
  const toNonprofitsError = errors.toNonprofits as unknown as FieldError;
  return toNonprofitsError?.message;
}

export function ToNonprofitWeights(props: ToNonptofitsWeightsProps) {
  const initialized = useInitializeWeights(props.fundraiser, props.form);
  const { toNonprofits = [] } = props.form.watch();

  const errorMessage = getErrorMessage(props.form.formState.errors);

  if (!initialized) {
    return null;
  }

  const hasCustomWeights = !!props.fundraiser.metadata?.forNonprofitWeights;
  const allowsNonprofitSelection =
    !!props.fundraiser.metadata?.showNonprofitCheckbox;
  const isPartialSelection =
    allowsNonprofitSelection &&
    toNonprofits.length < (props.fundraiser.forNonprofitIds?.length ?? 0);

  const onToggleNonprofit = (id: string) => {
    const newToNonprofits = toNonprofits.includes(id)
      ? toNonprofits.filter((toNonprofitId) => toNonprofitId !== id)
      : [...toNonprofits, id];
    props.form.setValue("toNonprofits", newToNonprofits);
    // right now set it to equal distribution
    props.form.setValue(
      "toNonprofitWeights",
      newToNonprofits.map((_) => 1)
    );
  };

  return (
    <div css={verticalStackCss.xs}>
      <div css={verticalStackCss.none}>
        <span css={{ color: `var(${colorCssVars.text.secondary})` }}>
          {hasCustomWeights
            ? "Donation split between"
            : isPartialSelection
            ? "Donation split evenly between"
            : "Donation split evenly between all"}
        </span>
        <FundraiserNonprofitsPopover
          showFooter={!hasCustomWeights}
          nonprofitIds={
            allowsNonprofitSelection
              ? props.fundraiser.forNonprofitIds || toNonprofits
              : toNonprofits
          }
          weights={
            hasCustomWeights
              ? props.fundraiser.metadata?.forNonprofitWeights
              : undefined
          }
          checkedIds={allowsNonprofitSelection ? toNonprofits : undefined}
          onToggle={allowsNonprofitSelection ? onToggleNonprofit : undefined}
        />
      </div>
      <MatchCapReachedMessage
        formContext={props.formContext}
        toNonprofits={toNonprofits}
      />
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </div>
  );
}

function useNonprofitsString({
  nonprofitIds,
}: {
  nonprofitIds?: string[] | null;
}) {
  const nonprofitsState = useContext(NonprofitsContext);
  if (!nonprofitIds) {
    return "the nonprofits";
  }
  const nonprofits = nonprofitIds
    .map((id) => getNonprofitOrUndefined(nonprofitsState, { id }))
    .filter((n): n is ContextNonprofit => !!n);
  return joinEnglishWordSeries(nonprofits.map((n) => n.name));
}

function MatchCapReachedMessage({
  formContext,
  toNonprofits,
}: {
  formContext: DonateFormContext;
  toNonprofits: string[];
}) {
  const { nonprofitMatchCampaign } = formContext;

  const availableAmountPerNonprofit =
    nonprofitMatchCampaign?.availableAmountPerNonprofit;

  const nonprofitsReachedCap =
    availableAmountPerNonprofit &&
    Object.keys(availableAmountPerNonprofit).filter((key) => {
      return (
        toNonprofits.includes(key) &&
        new Big(availableAmountPerNonprofit[key as Uuid] || 0).eq(0)
      );
    });

  const nonprofitsString = useNonprofitsString({
    nonprofitIds: nonprofitsReachedCap,
  });

  if (!nonprofitMatchCampaign || toNonprofits.length === 0) {
    return null;
  }

  if (nonprofitsReachedCap?.length) {
    return (
      <p>
        <BoldText>NOTE:</BoldText>{" "}
        {nonprofitsReachedCap.length === 1 ? nonprofitsString : "This campaign"}{" "}
        has reached its cap in matching funds. You can still donate to them but
        it will not be matched.
      </p>
    );
  }

  return null;
}

const BoldText = styled.span`
  font-weight: ${FontWeight.BOLD};
`;
